import { Component } from "react";
import { AlertTriangle, RefreshCw, ChevronDown } from "lucide-react";

// Generic crash fallback — calm message + Reload + a de-emphasized
// collapsible "Details" section carrying the raw error for screenshotting.
// Used both as the app-wide boundary (AppLayout, around <Outlet/>) and the
// nested Payment Desk boundary (around the active tab's content only).
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, showDetails: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ error, info });
  }

  render() {
    const { error, info, showDetails } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        className="rounded-lg border border-red-200 bg-red-50 p-6 max-w-xl mx-auto my-8"
        data-testid={this.props.testId || "error-boundary-fallback"}
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-slate-800">Something went wrong on this page.</p>
            <p className="text-xs text-slate-500 mt-1">
              Please try reloading. If it keeps happening, share the details below with support.
            </p>
            <button
              type="button"
              data-testid="error-boundary-reload-button"
              onClick={() => window.location.reload()}
              className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium px-3 py-1.5"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Reload
            </button>

            <button
              type="button"
              data-testid="error-boundary-details-toggle"
              onClick={() => this.setState({ showDetails: !showDetails })}
              className="mt-3 flex items-center gap-1 text-[11px] text-slate-400 hover:text-slate-600"
            >
              <ChevronDown className={`h-3 w-3 transition-transform ${showDetails ? "rotate-180" : ""}`} />
              Details
            </button>
            {showDetails && (
              <pre
                data-testid="error-boundary-details-content"
                className="mt-2 text-[11px] text-slate-500 bg-white border border-slate-200 rounded p-2 overflow-auto max-h-48 whitespace-pre-wrap"
              >
                {error?.message || String(error)}
                {info?.componentStack || ""}
              </pre>
            )}
          </div>
        </div>
      </div>
    );
  }
}
